import { collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import { auth, db } from "../firebase";
import { withTimeout } from "./promiseTimeout";

const ADMIN_APPS_URL =
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getAdminApps";
const APP_CACHE_TTL_MS = 5 * 60 * 1000;
const APP_STALE_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const APP_SESSION_CACHE_KEY = "cinema.admin.apps.v1";

let cachedApps = null;
let cacheExpiresAt = 0;
let inFlightRequest = null;
let requestGeneration = 0;

function readSessionCache({ allowStale = false } = {}) {
  const maxAgeMs = allowStale
    ? APP_STALE_CACHE_MAX_AGE_MS
    : APP_CACHE_TTL_MS;

  for (const storage of [window.sessionStorage, window.localStorage]) {
    try {
      const cached = JSON.parse(storage.getItem(APP_SESSION_CACHE_KEY));
      if (!cached || !Array.isArray(cached.data)) continue;

      const ageMs = Date.now() - Number(cached.savedAt || 0);
      if (ageMs <= maxAgeMs) return cached.data;
    } catch (error) {
      console.debug("Admin apps cache unavailable:", error?.message);
    }
  }

  return null;
}

function writeSessionCache(apps) {
  const serialized = JSON.stringify({ data: apps, savedAt: Date.now() });
  for (const storage of [window.sessionStorage, window.localStorage]) {
    try {
      storage.setItem(APP_SESSION_CACHE_KEY, serialized);
    } catch (error) {
      console.debug("Admin apps cache write skipped:", error?.message);
    }
  }
}

async function loadAppsFromApi() {
  const user =
    auth.currentUser ||
    (await withTimeout(
      new Promise((resolve, reject) => {
        let unsubscribe = () => {};
        unsubscribe = onAuthStateChanged(
          auth,
          (nextUser) => {
            unsubscribe();
            if (nextUser) {
              resolve(nextUser);
            } else {
              reject(new Error("Authentication required"));
            }
          },
          (authError) => {
            unsubscribe();
            reject(authError);
          },
        );
      }),
      "รอตรวจสอบสถานะผู้ดูแลระบบใช้เวลานานเกินไป",
      5000,
    ));
  const token = await withTimeout(
    user.getIdToken(),
    "ตรวจสอบสิทธิ์ผู้ดูแลระบบใช้เวลานานเกินไป",
    5000,
  );
  const response = await axios.post(
    ADMIN_APPS_URL,
    {},
    {
      timeout: 8000,
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.data?.success || !Array.isArray(response.data.data)) {
    throw new Error(response.data?.message || "โหลดรายการแอปไม่สำเร็จ");
  }

  return response.data.data;
}

async function loadAppsFromFirestore() {
  let timeoutId;
  try {
    return await Promise.race([
      getDocs(collection(db, "apps")).then((snapshot) =>
        snapshot.docs.map((appDoc) => ({
          key: appDoc.id,
          ...appDoc.data(),
        })),
      ),
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error("โหลดรายการแอปใช้เวลานานเกินไป")),
          7000,
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function getAppCreatedAtMillis(app) {
  const value = app?.createdAt;
  if (typeof value?.toMillis === "function") return value.toMillis();
  const seconds = Number(value?.seconds ?? value?._seconds);
  return Number.isFinite(seconds) ? seconds * 1000 : 0;
}

export function getCachedAppsSnapshot() {
  if (!Array.isArray(cachedApps)) {
    cachedApps = readSessionCache({ allowStale: true });
  }
  return cachedApps;
}

export async function getCachedApps({ force = false } = {}) {
  const now = Date.now();

  if (!cachedApps) {
    cachedApps = readSessionCache();
    if (cachedApps) cacheExpiresAt = now + APP_CACHE_TTL_MS;
  }

  if (!force && cachedApps && now < cacheExpiresAt) {
    return cachedApps;
  }

  if (!force && inFlightRequest) {
    return inFlightRequest;
  }

  const generation = ++requestGeneration;
  const request = loadAppsFromApi()
    .catch(async (apiError) => {
      console.warn("Admin apps API failed, using Firestore fallback:", apiError);
      try {
        return await loadAppsFromFirestore();
      } catch (firestoreError) {
        const staleApps = readSessionCache({ allowStale: true });
        if (staleApps) {
          console.warn("Using stale Admin apps cache after network failure");
          return staleApps;
        }
        throw firestoreError;
      }
    })
    .then((apps) => {
      if (generation === requestGeneration) {
        cachedApps = apps;
        cacheExpiresAt = Date.now() + APP_CACHE_TTL_MS;
        writeSessionCache(apps);
      }
      return apps;
    })
    .finally(() => {
      if (inFlightRequest === request) inFlightRequest = null;
    });

  inFlightRequest = request;
  return request;
}

export function invalidateAppsCache() {
  requestGeneration += 1;
  cachedApps = null;
  cacheExpiresAt = 0;
  inFlightRequest = null;
  try {
    window.sessionStorage.removeItem(APP_SESSION_CACHE_KEY);
    window.localStorage.removeItem(APP_SESSION_CACHE_KEY);
  } catch (error) {
    console.debug("Admin apps cache clear skipped:", error?.message);
  }
}

// Safari can freeze an in-flight request when a tab is backgrounded. React
// remounting does not reset module-level state, so allow the active page to
// start a fresh request while keeping already-rendered cached apps visible.
export function resetAppsInFlightRequest() {
  requestGeneration += 1;
  inFlightRequest = null;
  cacheExpiresAt = 0;
}
